export interface EventWizardDescriptionMarkdownProps {
  description: string;
  onUpdate: (desc: string) => void;
  isErrorState?: boolean;
}

export interface WordAtIndex {
  index: number; length: number; text: string
}

export interface WordNearIndex {
  wordFound?: WordAtIndex;
  startIndex: number;
  gap: number;
  currentIndex: number;
}

export interface EditableTheme {
  tooltip?: {
    edit: (mode?: string, preview?: string | null) => void;
  };
}

export const getPlainTextFromMarkdown = (text: string): string => {
  return text.replace(/<[^>]+>/g, '');
};

export const removeLineSpaces = (text: string): string => {
  return text.replace(/\s+/g, ' ');
};

export const MARKDOWN_EDITOR_LINK_TOOLTIP_TIMER = 2000;
const MAX_CURSOR_TO_WORD_GAP = 2;

const isWhitespaceCharacter = (text: string): boolean => {
  return /\s/.test(text);
};

const getWordAtIndex = (editorFullText: string, index: number): WordAtIndex | undefined => {
  const textLength = editorFullText.length;

  if (index < 0 || index >= textLength || /\s/.test(editorFullText[index])) {
    return undefined;
  }

  let start = index;
  let end = index;

  while (start > 0 && !isWhitespaceCharacter(editorFullText[start - 1])) {
    start -= 1;
  }

  while (end < textLength && !isWhitespaceCharacter(editorFullText[end])) {
    end += 1;
  }

  return {
    index: start,
    length: end - start,
    text: editorFullText.slice(start, end),
  };
};

const getNearbyWordToLeftOfCursor = (editorText: string, currentCursorIndex: number): WordNearIndex => {
  let leftIndex = currentCursorIndex - 1;
  let leftGap = 0;
  let leftWord;

  while (leftIndex >= 0 && leftGap < MAX_CURSOR_TO_WORD_GAP) {
    leftWord = getWordAtIndex(editorText, leftIndex);

    if (leftWord) {
      break;
    }

    leftIndex -= 1;
    leftGap += 1;
  }

  return {
    wordFound: leftWord,
    startIndex: currentCursorIndex,
    gap: leftGap,
    currentIndex: leftIndex,
  };
}

const getNearbyWordToRightOfCursor = (editorText: string, currentCursorIndex: number): WordNearIndex => {
  const textLength = editorText.length;
  let rightIndex = currentCursorIndex + 1;
  let rightGap = 0;
  let rightWord;

  while (rightIndex < textLength && rightGap < MAX_CURSOR_TO_WORD_GAP) {
    rightWord = getWordAtIndex(editorText, rightIndex);

    if (rightWord) {
      break;
    }

    rightIndex += 1;
    rightGap += 1;
  }

  return {
    wordFound: rightWord,
    startIndex: currentCursorIndex,
    gap: rightGap,
    currentIndex: rightIndex,
  };
};


export const getWordNearCursor = (editorText: string, cursorIndex: number): WordAtIndex | undefined => {
  const textLength = editorText.length;

  if (textLength === 0 || cursorIndex === 0) {
    return undefined;
  }

  const directWord = getWordAtIndex(editorText, cursorIndex);

  if (directWord) {
    return directWord;
  }

  const leftWordData = getNearbyWordToLeftOfCursor(editorText, cursorIndex);
  const leftWord = leftWordData.wordFound;
  const rightWordData = getNearbyWordToRightOfCursor(editorText, cursorIndex);
  const rightWord = rightWordData.wordFound;

  if (leftWord && rightWord) {
    return leftWordData.gap <= rightWordData.gap ? leftWord : rightWord;
  }

  return leftWord ?? rightWord;
};